import React, { useState, useEffect } from 'react';
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { get } from '../../config/axios';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';

const CbtAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    overview: null,
    sessionOutcomes: null,
    userEngagement: null,
    feedback: null,
    sessionTypes: null
  });
  const [timeFilter, setTimeFilter] = useState('thismonth');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCBTAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [overview, outcomes, engagement, feedback, sessionTypes] = await Promise.all([
        get(`/api/dashboard/cbt/analytics/overview?timeFilter=${timeFilter}`),
        get(`/api/dashboard/cbt/analytics/session-outcomes?timeFilter=${timeFilter}`),
        get(`/api/dashboard/cbt/analytics/user-engagement?timeFilter=${timeFilter}`),
        get(`/api/dashboard/cbt/analytics/feedback?timeFilter=${timeFilter}`),
        get(`/api/dashboard/cbt/analytics/session-types?timeFilter=${timeFilter}`)
      ]);

      // Log API responses for debugging
      console.log('API Responses:', {
        overview: overview,
        outcomes: outcomes,
        engagement: engagement,
        feedback: feedback,
        sessionTypes: sessionTypes
      });

      // Check if all API calls were successful
      if (overview.success && outcomes.success && engagement.success && 
          feedback.success && sessionTypes.success) {
        setAnalyticsData({
          overview: overview.data,
          sessionOutcomes: outcomes.data,
          userEngagement: engagement.data,
          feedback: feedback.data,
          sessionTypes: sessionTypes.data
        });
      } else {
        console.error('API call failures:', {
          overview: overview.success,
          outcomes: outcomes.success,
          engagement: engagement.success,
          feedback: feedback.success,
          sessionTypes: sessionTypes.success
        });
        throw new Error('One or more API calls failed');
      }
    } catch (error) {
      console.error('Error fetching CBT analytics:', error);
      setError('Failed to load CBT analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCBTAnalytics();
  }, [timeFilter]);

  const handleTimeFilterChange = (event) => {
    setTimeFilter(event.target.value);
  };

  const getTimeFilterLabel = (filter) => {
    const labels = {
      today: 'Today',
      thisweek: 'This Week',
      thismonth: 'This Month',
      last7days: 'Last 7 Days',
      last30days: 'Last 30 Days',
      thisyear: 'This Year'
    };
    return labels[filter] || 'This Month';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading CBT Analytics...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Layout>
      <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          CBT Analytics Dashboard
        </Typography>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={timeFilter}
            onChange={handleTimeFilterChange}
            label="Time Period"
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="thisweek">This Week</MenuItem>
            <MenuItem value="thismonth">This Month</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="last30days">Last 30 Days</MenuItem>
            <MenuItem value="thisyear">This Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h6" color="textSecondary" gutterBottom>
        {getTimeFilterLabel(timeFilter)} Overview
      </Typography>

      {/* Overview Cards */}
      {analyticsData.overview && analyticsData.overview.overview && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Sessions
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {analyticsData.overview.overview.totalSessions}
                    </Typography>
                  </Box>
                  <AssessmentIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e8', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Completion Rate
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {analyticsData.overview.overview.completionRate}%
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#388e3c' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Average Rating
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {analyticsData.overview.overview.averageRating}/5
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: 40, color: '#f57c00' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fce4ec', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Feedbacks
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight="bold">
                      {analyticsData.overview.overview.totalFeedbacks}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: '#c2185b' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Session Outcomes Chart */}
        {analyticsData.sessionOutcomes && analyticsData.sessionOutcomes.chartData && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {analyticsData.sessionOutcomes.chartTitle}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData.sessionOutcomes.chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ label, percentage }) => `${label}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="percentage"
                    >
                      {analyticsData.sessionOutcomes.chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Session Types Chart */}
        {analyticsData.sessionTypes && analyticsData.sessionTypes.chartData && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {analyticsData.sessionTypes.chartTitle}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData.sessionTypes.chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ label, percentage }) => `${label}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="percentage"
                    >
                      {analyticsData.sessionTypes.chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}


        {/* User Engagement Metrics */}
        {analyticsData.userEngagement && analyticsData.userEngagement.engagement && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Engagement Metrics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {analyticsData.userEngagement.engagement.totalUsers}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Users
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {analyticsData.userEngagement.engagement.activeUsers}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Users
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        {analyticsData.userEngagement.engagement.engagementRate}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Engagement Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" color="info.main" fontWeight="bold">
                        {analyticsData.userEngagement.engagement.homeworkCompletionRate}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Homework Completion
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Summary Stats */}
      {analyticsData.feedback && analyticsData.feedback.summary && (
        <Box mt={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Satisfaction Metrics
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip 
                  label={`Helpful for Therapy: ${analyticsData.feedback.summary.helpfulForTherapyRate}%`}
                  color="success"
                  variant="outlined"
                />
                <Chip 
                  label={`Info Clear: ${analyticsData.feedback.summary.infoClearRate}%`}
                  color="info"
                  variant="outlined"
                />
                <Chip 
                  label={`Felt Engaged: ${analyticsData.feedback.summary.feltEngagedRate}%`}
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
    </Layout>
  );
};

export default CbtAnalytics;
